import React, { useEffect, useState, useCallback } from "react";
import * as Utils from "../utils";
import { api, ApiError } from "../api/client";
import {
  isBiometricAvailable,
  getBiometricLabel,
  hasBiometricVault,
  enrollBiometric,
  unlockBiometric,
  clearBiometricVault,
} from "../utils/biometric";
import LogoUssBlu from "../assets/LogoUssBlu.png";

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [toast, setToast] = useState(null);
  const [biometricReady, setBiometricReady] = useState(false);
  const [biometricLabel, setBiometricLabel] = useState("biometrico");
  const [vaultPresent, setVaultPresent] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [enrollPrompt, setEnrollPrompt] = useState(null); // { user, password }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const available = await isBiometricAvailable();
      if (cancelled) return;
      setBiometricReady(available);
      if (available) {
        const label = await getBiometricLabel();
        if (!cancelled && label) setBiometricLabel(label);
      }
      setVaultPresent(hasBiometricVault());
    })();
    return () => { cancelled = true; };
  }, []);

  const finalizeLogin = useCallback(async (user, plainPassword, savedUsername) => {
    const userAggiornato = await Utils.aggiornaResidui(user);
    // sync_timbrate_cedo è NOT NULL lato Cedolino: un null è una scelta deliberata (colonna
    // sbloccata a mano per un'azienda specifica), mai il default. Significa "bypassa il
    // controllo GPS" per chi ha problemi di calcolo/dispositivo - va combinato col routing
    // Cedolino invariato (sync_timbrate_cedo forzato a true), non con quello su DB EasyJob.
    // Verificato su DEV: un NULL lato Cedolino arriva come campo assente (undefined) nella
    // risposta di login, non come null esplicito - "== null" copre entrambi i casi.
    const gpsCheckDisabilitato = userAggiornato.sync_timbrate_cedo == null;
    const userConPermessi = {
      ...userAggiornato,
      permessi: user.permessi,
      sync_timbrate_cedo: gpsCheckDisabilitato ? true : userAggiornato.sync_timbrate_cedo,
      gps_check_disabilitato: gpsCheckDisabilitato,
    };
    localStorage.setItem("easyjob_user", JSON.stringify({ ...userConPermessi, password: plainPassword }));
    await Utils.saveUserOffline({ ...userConPermessi, password: plainPassword, username: savedUsername });
    return userConPermessi;
  }, []);

  const performLogin = useCallback(async (loginUsername, loginPassword, { fromVault = false } = {}) => {
    const resJson = await api.post("/api/login", { username: loginUsername, password: loginPassword });
    const user = resJson?.user;
    if (!user?.assunzione_id) {
      throw new Error("Nessun Dipendente associato a questo utente.\nContattare un responsabile.");
    }
    const userConPermessi = await finalizeLogin(user, loginPassword, loginUsername);
    return { userConPermessi, fromVault };
  }, [finalizeLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    try {
      const { userConPermessi } = await performLogin(username, password);
      // Se siamo in APK + biometrico disponibile + nessun vault esistente → proponi enrollment
      if (biometricReady && !hasBiometricVault()) {
        setEnrollPrompt({ user: userConPermessi, username, password });
        return;
      }
      onLogin(userConPermessi);
    } catch (err) {
      if (err?.message?.startsWith("Nessun Dipendente")) {
        setFeedback(err.message);
        return;
      }
      if (err instanceof ApiError && !err.isNetworkError) {
        setFeedback(err.message || "Credenziali non valide");
        return;
      }
      // Errore di rete: prova fallback offline
      let user = null;
      try { user = await Utils.getUserOffline(); } catch {}
      if (!user) {
        const userData = localStorage.getItem("easyjob_user");
        if (userData) {
          try {
            user = JSON.parse(userData);
          } catch {
            localStorage.removeItem("easyjob_user");
          }
        }
      }
      if (user && user.username === username && user.password === password) {
        onLogin(user);
      } else if (user) {
        setFeedback("Credenziali non valide");
      } else {
        setFeedback("Nessun utente salvato per l'accesso offline");
      }
    }
  };

  const handleBiometricUnlock = async () => {
    setFeedback(null);
    setBiometricBusy(true);
    try {
      const creds = await unlockBiometric();
      try {
        const { userConPermessi } = await performLogin(creds.username, creds.password, { fromVault: true });
        onLogin(userConPermessi);
      } catch (loginErr) {
        // Se il backend rifiuta le credenziali salvate (es. password cambiata) → invalida il vault
        if (loginErr instanceof ApiError && !loginErr.isNetworkError) {
          await clearBiometricVault();
          setVaultPresent(false);
          setFeedback("Le credenziali salvate non sono più valide. Inserisci la password.");
          return;
        }
        // Errore di rete: tentativo offline
        let user = null;
        try { user = await Utils.getUserOffline(); } catch {}
        if (user && user.username === creds.username && user.password === creds.password) {
          onLogin(user);
          return;
        }
        setFeedback("Errore di rete. Riprova.");
      }
    } catch (err) {
      // L'utente ha annullato lo sblocco biometrico oppure verifica fallita
      if (err?.code === "10" || err?.message?.toLowerCase?.()?.includes("cancel")) {
        return;
      }
      setFeedback("Sblocco biometrico fallito. Usa la password.");
    } finally {
      setBiometricBusy(false);
    }
  };

  const handleEnrollAccept = async () => {
    if (!enrollPrompt) return;
    try {
      await enrollBiometric(enrollPrompt.username, enrollPrompt.password);
      setVaultPresent(true);
      setToast({ message: `Accesso ${biometricLabel} attivato` });
      setTimeout(() => setToast(null), 1500);
    } catch (err) {
      console.warn("Enroll biometrico fallito:", err);
    } finally {
      const user = enrollPrompt.user;
      setEnrollPrompt(null);
      onLogin(user);
    }
  };

  const handleEnrollDecline = () => {
    if (!enrollPrompt) return;
    const user = enrollPrompt.user;
    setEnrollPrompt(null);
    onLogin(user);
  };

  // Auto-trigger sblocco biometrico al primo mount se vault presente (UX banking-app)
  // Delay 500ms per dare tempo al render di completarsi prima di aprire il prompt nativo.
  useEffect(() => {
    if (!biometricReady || !vaultPresent || biometricBusy || enrollPrompt) return;
    const timer = setTimeout(() => {
      handleBiometricUnlock();
    }, 500);
    return () => clearTimeout(timer);
  }, [biometricReady, vaultPresent]); // eslint-disable-line

  return (
    <>
      <div className="app-bar bg-blu-uss">
        <h1 className="text-lg sm:text-xl font-bold flex-1 text-white">EasyJob - Login</h1>
      </div>
      <form
        onSubmit={handleSubmit}
        className="max-w-sm mx-auto mt-10 flex flex-col gap-6 bg-white p-8 rounded-xl shadow-lg"
      >
        <img src={LogoUssBlu} alt="Logo aziendale" className="mx-auto mb-6 w-28 h-28 object-contain" />
        <h2 className="text-3xl font-bold text-primary text-center mb-6">EasyJob - Login</h2>

        {biometricReady && vaultPresent && (
          <button
            type="button"
            onClick={handleBiometricUnlock}
            disabled={biometricBusy}
            className="bg-blue-50 border border-primary text-primary rounded-lg px-4 py-3 text-lg font-semibold flex items-center justify-center gap-2"
          >
            {biometricBusy ? "Sblocco in corso..." : `🔒 Sblocca con ${biometricLabel}`}
          </button>
        )}

        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          required
          className="border rounded-lg px-4 py-3 text-lg"
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
          className="border rounded-lg px-4 py-3 text-lg"
        />
        <button type="submit" className="bg-primary text-white rounded-lg px-4 py-3 text-lg font-semibold shadow hover:bg-blue-800 transition">
          Login
        </button>
        {feedback && <div className="text-red-600 text-base text-center">{feedback}</div>}
      </form>

      {enrollPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-xl font-bold text-primary mb-3">Accesso rapido</h3>
            <p className="text-gray-700 mb-5">
              Vuoi attivare l'accesso con <strong>{biometricLabel}</strong> per i prossimi login?
              Le tue credenziali resteranno cifrate sul dispositivo.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleEnrollDecline}
                className="flex-1 border border-gray-300 text-gray-700 rounded-lg px-4 py-3 font-semibold"
              >
                No, grazie
              </button>
              <button
                type="button"
                onClick={handleEnrollAccept}
                className="flex-1 bg-primary text-white rounded-lg px-4 py-3 font-semibold"
              >
                Attiva
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div
            className="px-8 py-4 rounded-xl shadow-lg text-white text-center text-lg font-semibold bg-green-600"
            style={{ minWidth: 220, maxWidth: 320 }}
          >
            {toast.message}
          </div>
        </div>
      )}
    </>
  );
}

export default Login;
